/**
 * shared/space_catalog.ts — CLIENT MIRROR of server/src/lib/space_catalog.ts (the server copy is
 * authoritative; keep them byte-identical below the headers — `verify:space-ledger` diffs them).
 */
import { marketUnitPrice, stationDrift } from './space_industry';
import { applyPriceMod, depthMult } from './space_economy';

// ── THE EXCHANGE CATALOG (space campaign A, 2026-09-30; space.md §14 gap 20) ─────────────────────────
// The space Exchange (spaceMarketBuy / spaceMarketSell — the platform counterparty with regional marks) trades
// what a HOLD can carry: the commodity each of the fifteen resource families bridges to (RES_FAMILIES[i].commodity,
// one per family since the society wave). EXCHANGE_GOODS is the ONE projection of the server's catalog —
// `projectExchange(COMMODITIES, RES_FAMILIES)` — written out so the client needs no copy of the 57-commodity
// table; the harness pins the literal to the projection, so a catalog change that is not carried here fails the
// gate instead of drifting (the old dock list offered "Luxuries", which is no commodity, and four wrong bases).
// The server's COMMODITIES stays the price authority.

export type ExchangeTier = 'raw' | 'intermediate' | 'staple' | 'luxury';
export interface ExchangeGood {
  /** The commodity key the Exchange reducers take. */
  commodity: string;
  /** What a hold carries under it (the family's name). */
  label: string;
  /** COMMODITIES[commodity].baseValue — the mark is baseValue × MARKET_PRICE_SCALE × drift. */
  baseValue: number;
  tier: ExchangeTier;
  /** The resource families whose lots sell as this commodity (the purchase mints the first). */
  families: readonly string[];
}

export const EXCHANGE_GOODS: readonly ExchangeGood[] = [
  { commodity: 'ore', label: 'Ferrite Ore', baseValue: 5, tier: 'raw', families: ['ferrite_ore'] },
  { commodity: 'noble_ore', label: 'Noble Ore', baseValue: 11, tier: 'raw', families: ['noble_ore'] },
  { commodity: 'crystal', label: 'Crystal', baseValue: 9, tier: 'raw', families: ['crystal'] },
  { commodity: 'stone', label: 'Stone Grade', baseValue: 4, tier: 'raw', families: ['stone'] },
  { commodity: 'reagent', label: 'Reagent', baseValue: 6, tier: 'raw', families: ['reagent'] },
  { commodity: 'gas', label: 'Rare Gas', baseValue: 8, tier: 'raw', families: ['gas'] },
  { commodity: 'solvent', label: 'Solvent', baseValue: 6, tier: 'raw', families: ['solvent'] },
  { commodity: 'grain', label: 'Grain', baseValue: 3, tier: 'raw', families: ['grain'] },
  { commodity: 'produce', label: 'Produce', baseValue: 4, tier: 'raw', families: ['produce'] },
  { commodity: 'game', label: 'Game Meat', baseValue: 5, tier: 'raw', families: ['game'] },
  { commodity: 'fish', label: 'Brine Catch', baseValue: 4, tier: 'raw', families: ['brine_catch'] },
  { commodity: 'fiber', label: 'Fiber', baseValue: 4, tier: 'raw', families: ['fiber'] },
  { commodity: 'hide', label: 'Hide', baseValue: 6, tier: 'raw', families: ['hide'] },
  { commodity: 'timber', label: 'Timber', baseValue: 5, tier: 'raw', families: ['timber'] },
  { commodity: 'resin', label: 'Resin', baseValue: 5, tier: 'raw', families: ['resin'] },
];

/** The projection itself (pure): every commodity a family bridges to, in family order, merged by commodity. */
export function projectExchange(
  commodities: Readonly<Record<string, { baseValue: number; tier: string }>>,
  families: readonly { key: string; label: string; commodity: string }[],
): ExchangeGood[] {
  const out: ExchangeGood[] = [];
  for (const f of families) {
    const def = commodities[f.commodity];
    if (!def) continue;
    const have = out.find((g) => g.commodity === f.commodity);
    if (have) { (have.families as string[]).push(f.key); continue; }
    out.push({ commodity: f.commodity, label: f.label, baseValue: def.baseValue, tier: def.tier as ExchangeTier, families: [f.key] });
  }
  return out;
}

export function exchangeGood(commodity: string): ExchangeGood | null {
  for (const g of EXCHANGE_GOODS) if (g.commodity === commodity) return g;
  return null;
}
/** The commodity a family's lots sell as on the Exchange (null: not traded there). */
export function commodityOfFamily(familyKey: string): string | null {
  for (const g of EXCHANGE_GOODS) if (g.families.includes(familyKey)) return g.commodity;
  return null;
}
/** Why a commodity key is not on the Exchange (null when it is). */
export function exchangeRefusal(commodity: string): string | null {
  if (exchangeGood(commodity)) return null;
  if (commodity === 'luxury') return 'Luxuries are no commodity any more (the society wave split them into noble ore, crystal and the crafted luxuries) — the Exchange trades the fifteen resource families a hold carries';
  const name = commodity.replace(/_/g, ' ');
  return `The Exchange does not trade ${name} — it deals only in the fifteen resource families a hold carries; ${name} trades on a town's market`;
}
/** Goods the old dock list offered that the Exchange does not trade, with the reason (the dock prints them). */
export const NOT_ON_EXCHANGE: readonly { commodity: string; why: string }[] = [
  { commodity: 'luxury', why: 'no commodity since the society wave — noble ore and crystal have their own rows' },
  { commodity: 'herbs', why: 'a ground good no resource family carries — reagent, gas and solvent have their own rows' },
];

/** One Exchange unit price — drift × the yard's shortage mod × the epoch's depth (the reducers' own composition). */
export function exchangeUnitPrice(
  baseValue: number, stationKey: string, commodity: string, epoch: number, sysTier: 1 | 2 | 3, comTier: string,
  mod: number, depthUnits: number, side: 'buy' | 'sell',
): bigint {
  const drift = stationDrift(stationKey, commodity, epoch, sysTier, comTier);
  return applyPriceMod(applyPriceMod(marketUnitPrice(baseValue, drift, side), mod), depthMult(side, depthUnits));
}

// ── HONEST QUOTES: the order book (report §17.1) ────────────────────────────────────────────────────
// The numbers placeMarketOrder / settleFill use (space_market.ts; the harness pins the literals): the broker
// takes max(150, 300 − 30 × Trade) bps of the ORDER value at placement (at least 1 ⓒ, never refunded); every
// FILL pays 5 % of its gross from the SELLER's proceeds, rounded DOWN — so a fill under 20 ⓒ pays no tax, and a
// sale split into small fills pays less than one big fill (documented; a seller-level accumulator would be a
// versioned balance change — not made here).
export const ORDER_FEES = { brokerBaseBps: 300, brokerPerTradeLevelBps: 30, brokerFloorBps: 150, brokerMinSov: 1n, fillTaxBps: 500, taxFreeBelowSov: 20n } as const;

export function brokerFeeBps(tradeLevel: number): number {
  return Math.max(ORDER_FEES.brokerFloorBps, ORDER_FEES.brokerBaseBps - ORDER_FEES.brokerPerTradeLevelBps * Math.max(0, Math.floor(tradeLevel)));
}
export function brokerFee(orderValue: bigint, tradeLevel: number): bigint {
  const f = (orderValue * BigInt(brokerFeeBps(tradeLevel))) / 10_000n;
  return f > 0n ? f : ORDER_FEES.brokerMinSov;
}
export function fillTax(gross: bigint): bigint {
  return (gross * BigInt(ORDER_FEES.fillTaxBps)) / 10_000n;
}
export interface OrderQuote {
  value: bigint; broker: bigint; brokerBps: number;
  /** The tax if the whole order fills at once at its own price (a sell: yours; a buy: the seller's). */
  taxOneFill: bigint;
  /** A sell: what you receive if it fills at once at your price, after tax. A buy: the escrow + broker today. */
  net: bigint;
  taxFree: boolean;
  lines: string[];
}
const pct = (bps: number): string => `${(bps / 100).toFixed(bps % 100 ? 1 : 0)} %`;
export function orderQuote(side: 'buy' | 'sell', priceSov: bigint, qty: number, tradeLevel: number): OrderQuote {
  const value = priceSov * BigInt(Math.max(0, Math.floor(qty)));
  const brokerBps = brokerFeeBps(tradeLevel);
  const broker = brokerFee(value, tradeLevel);
  const taxOneFill = fillTax(value);
  const taxFree = taxOneFill === 0n;
  const lines: string[] = [];
  lines.push(`Broker fee ${broker} ⓒ, paid now: ${pct(brokerBps)} of ${value} ⓒ${tradeLevel > 0 ? ` (Trade ${tradeLevel})` : ''}, at least 1 ⓒ, never refunded.`);
  if (side === 'sell') {
    lines.push(`Fill tax ${pct(ORDER_FEES.fillTaxBps)} of each fill, from your proceeds: ${taxOneFill} ⓒ if it all fills at ${priceSov} ⓒ → you receive ${value - taxOneFill} ⓒ. A higher resting bid fills at the bid's price.`);
  } else {
    lines.push(`You escrow ${value} ⓒ for the part that rests (returned on cancel or expiry); a fill against a resting ask pays the ask, never more than ${priceSov} ⓒ a unit. The SELLER pays the ${pct(ORDER_FEES.fillTaxBps)} fill tax.`);
  }
  lines.push(`A fill under ${ORDER_FEES.taxFreeBelowSov} ⓒ pays no tax (5 % rounds down to 0), so a sale split into small fills pays less tax than one large fill.`);
  const net = side === 'sell' ? value - taxOneFill : value + broker;
  return { value, broker, brokerBps, taxOneFill, net, taxFree, lines };
}

// ── HONEST QUOTES: hull insurance and what a loss costs (report §7.1, §17.1) ───────────────────────
// insureShip / destroyShipCore (space_market.ts): premium 15 % of the hull's price, payout 40 % on destruction,
// 7-day term, one policy per hull, the HULL only. The fit goes down with her; the cargo aboard lies in a wreck
// (THE WRECK LAW, 2 h, anyone in range strips it); a courier crate aboard fails its contract (the issuer gets the
// reward and your collateral); a port crate aboard is the port's (its run is lost, its reward returns to the
// port). A group's SRP may top up. The stock Lancer costs nothing, cannot be insured, and is re-issued free when
// the last hull is lost (THE STARTER LAW).
export const INSURANCE = { premiumBps: 1500n, payoutBps: 4000n, ttlS: 7 * 86_400, srpShareBps: 6000n, wreckTtlS: 2 * 3600 } as const;

export function insuranceQuote(hullCostSov: bigint): { insurable: boolean; premium: bigint; payout: bigint; days: number } {
  const insurable = hullCostSov > 0n;
  return {
    insurable,
    premium: insurable ? (hullCostSov * INSURANCE.premiumBps) / 10_000n : 0n,
    payout: insurable ? (hullCostSov * INSURANCE.payoutBps) / 10_000n : 0n,
    days: INSURANCE.ttlS / 86_400,
  };
}

export interface LossFacts {
  hullName: string; hullCostSov: bigint;
  /** The standing policy's payout, or null when uninsured / expired. */
  policyPayout: bigint | null;
  fittedModules: number; ownUnits: number; courierCrates: number; portUnits: number;
}
export interface LossPreview { pays: string; notCovered: string[]; also: string | null; starter: string | null }
export function lossPreview(f: LossFacts): LossPreview {
  const q = insuranceQuote(f.hullCostSov);
  let pays: string;
  if (!q.insurable) pays = `A loss pays nothing — the stock ${f.hullName} is free and cannot be insured.`;
  else if (f.policyPayout !== null) pays = `A loss pays ${f.policyPayout} ⓒ (40 % of the ${f.hullCostSov} ⓒ hull) while the policy stands.`;
  else pays = `Uninsured: a loss pays nothing. Insuring costs ${q.premium} ⓒ for ${q.days} days and pays ${q.payout} ⓒ (40 % of the hull) on destruction.`;
  const notCovered: string[] = [];
  notCovered.push(f.fittedModules > 0 ? `the fit — ${f.fittedModules} module${f.fittedModules === 1 ? '' : 's'} go down with her` : 'the fit (nothing fitted now)');
  if (f.ownUnits > 0) notCovered.push(`your ${f.ownUnits} u of cargo — it lies in a wreck for ${INSURANCE.wreckTtlS / 3600} h and anyone in range may strip it`);
  else notCovered.push('any cargo aboard — it lies in a wreck anyone may strip');
  if (f.courierCrates > 0) notCovered.push(`${f.courierCrates} courier crate${f.courierCrates === 1 ? '' : 's'} — the contract fails and your collateral goes to the issuer`);
  if (f.portUnits > 0) notCovered.push(`the port's ${f.portUnits} u — the port's run is lost (its reward goes back to the port)`);
  const also = q.insurable ? 'A group replacement program (SRP), if yours runs one, may add up to 60 % of the hull, capped by its officers.' : null;
  const starter = q.insurable ? null : 'The stock Lancer is re-issued free when your last hull is lost.';
  return { pays, notCovered, also, starter };
}
